import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import BackupExcelButton from "@/components/BackupExcelButton";
import { toast } from "sonner";
import { Trash2, Pencil, Download, X } from "lucide-react";
import { DeskripsiButton, AutoGrowTextarea } from "@/components/Deskripsi";
import { computeAggregate, groupNilai, letterColor, TAHUN_AJARAN } from "@/pages/akademik/_nilaiHelpers";
import { downloadNilaiPdf } from "@/pages/akademik/Nilai";

const F_EMPTY = { siswa: "", jenis: "", tahun_ajaran: "", semester: "", mapel: "" };

function EditNilaiModal({ entry, onClose, onSaved }) {
  const [skor, setSkor] = useState(String(entry?.skor ?? ""));
  const [deskripsi, setDeskripsi] = useState(entry?.deskripsi || "");
  const [busy, setBusy] = useState(false);
  if (!entry) return null;
  const isUtsUas = entry.jenis_nilai === "uts" || entry.jenis_nilai === "uas";

  const save = async () => {
    if (skor === "" || Number(skor) < 0 || Number(skor) > 100) {
      toast.error("Skor harus 0-100"); return;
    }
    setBusy(true);
    try {
      const payload = {
        siswa_id: entry.siswa_id,
        jenis: entry.jenis,
        tahun_ajaran: entry.tahun_ajaran,
        semester: entry.semester,
        mata_pelajaran: entry.mata_pelajaran,
        jenis_nilai: entry.jenis_nilai || null,
        nomor: entry.nomor || null,
        materi: entry.materi || null,
        aktifitas: entry.aktifitas || null,
        bulan: entry.bulan || null,
        nilai_ke: entry.nilai_ke || null,
        skor: Number(skor),
        deskripsi: isUtsUas ? deskripsi : null,
      };
      await api.put(`/nilai/${entry.id}`, payload);
      toast.success("Nilai diperbarui");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-nilai-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Edit Nilai</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="space-y-3 mb-4 text-sm">
          <div><b>Siswa:</b> {entry.nama_siswa}</div>
          <div><b>Mapel:</b> {entry.mata_pelajaran}</div>
          <div><b>Jenis Nilai:</b> {entry.jenis_nilai || (entry.bulan ? `bln-${entry.bulan}` : "—")}{entry.nomor ? ` #${entry.nomor}` : ""}</div>
        </div>
        <label className="pp-label">Skor (0-100)</label>
        <input type="number" min={0} max={100} step="0.01" className="pp-input" value={skor} onChange={(e) => setSkor(e.target.value)} data-testid="edit-nilai-skor" />
        {isUtsUas && (
          <div className="mt-3">
            <label className="pp-label">Deskripsi</label>
            <AutoGrowTextarea value={deskripsi} onChange={setDeskripsi} placeholder="Tulis deskripsi UTS/UAS…" dataTestid="edit-nilai-deskripsi" />
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="pp-btn-secondary">Batal</button>
          <button onClick={save} disabled={busy} className="pp-btn-primary" data-testid="save-edit-nilai">{busy ? "Menyimpan…" : "Simpan"}</button>
        </div>
      </div>
    </div>
  );
}

export default function NilaiKepsek() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [editEntry, setEditEntry] = useState(null);

  const load = useCallback(async () => { try { const { data } = await api.get("/nilai"); setAll(data); } catch (e) { console.error(e); } }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((e) => {
    if (applied.siswa && !(e.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.jenis && e.jenis !== applied.jenis) return false;
    if (applied.tahun_ajaran && e.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && e.semester !== applied.semester) return false;
    if (applied.mapel && !e.mata_pelajaran.toLowerCase().includes(applied.mapel.toLowerCase())) return false;
    return true;
  });

  const del = async (id) => {
    if (!window.confirm("Hapus nilai ini?")) return;
    try { await api.delete(`/nilai/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error("Gagal hapus"); }
  };

  const groups = groupNilai(items);

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Nilai Siswa (Semua)" action={
        <div className="flex gap-2">
          <BackupExcelButton endpoint="nilai" filters={{ tahun_ajaran: applied.tahun_ajaran, semester: applied.semester, jenis: applied.jenis, mata_pelajaran: applied.mapel }} testId="backup-nilai-btn" />
          <button onClick={() => downloadNilaiPdf(applied, items)} className="pp-btn-secondary" data-testid="dl-nilai-pdf-kepsek">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      } />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="nilai-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari siswa" },
          { key: "jenis", type: "select", label: "Jenis", options: [{ v: "akademik", label: "Akademik" }, { v: "non_akademik", label: "Non-Akademik" }, { v: "ekstra", label: "Ekstra" }] },
          { key: "tahun_ajaran", type: "select", label: "TA", options: TAHUN_AJARAN },
          { key: "semester", type: "select", label: "Sem", options: [{ v: "I", label: "I" }, { v: "II", label: "II" }] },
          { key: "mapel", type: "text", label: "Mapel", placeholder: "Cari mapel" },
        ]}
      />
      {groups.length === 0 && <section className="pp-card p-10 text-center italic text-slate-400">Belum ada nilai.</section>}
      <div className="space-y-6">
        {groups.map((g) => {
          const agg = computeAggregate(g);
          return (
            <section key={g.key} className="pp-card overflow-hidden">
              <header className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{g.nama_siswa} · {g.mata_pelajaran}</div>
                  <div className="text-xs text-slate-500 capitalize">{g.jenis.replace("_", " ")} · {g.tahun_ajaran} · Sem {g.semester}</div>
                </div>
                {agg.letter && agg.letter !== "—" && <span className={`px-3 py-1 rounded font-bold ${letterColor(agg.letter)}`}>{agg.letter}</span>}
              </header>
              <div className="p-5">
                <div className="text-xs text-slate-600 mb-2">Jumlah entry: {g.entries.length}</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {g.entries.map((e) => (
                    <span key={e.id} className="px-2 py-1 rounded bg-slate-100 border border-slate-200 inline-flex items-center gap-1.5">
                      <span className="text-slate-600">{e.jenis_nilai || (e.bulan ? `bln-${e.bulan}` : "—")}{e.nomor ? ` #${e.nomor}` : ""}{e.nilai_ke ? ` n${e.nilai_ke}` : ""}:</span>
                      <span className="font-semibold">{e.skor?.toFixed(1)}</span>
                      {e.deskripsi && <DeskripsiButton text={e.deskripsi} dataTestid={`deskripsi-kepsek-${e.id}`} />}
                      <button onClick={() => setEditEntry(e)} className="text-indigo-400 hover:text-indigo-600" data-testid={`edit-nilai-${e.id}`}><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => del(e.id)} className="text-rose-400 hover:text-rose-600" data-testid={`del-nilai-${e.id}`}><Trash2 className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>
      <EditNilaiModal entry={editEntry} onClose={() => setEditEntry(null)} onSaved={() => { setEditEntry(null); load(); }} />
    </Layout>
  );
}
